import React from 'react';
import {
  PlayCircle, RotateCcw, CheckCircle2, Flag, Trash2, Eye, Settings, MoreHorizontal,
} from 'lucide-react';
import { RunStatePill } from '../EtlPages';
import { ActionSheet, SheetKv, SheetActions, SheetAction, clockTime, timeAgo } from './EtlMobileKit';
import { issueFileName, stageLabel } from './DlqCardList';

const EXTRACTION_LABEL = {
  textract: 'Textract OCR',
  formRecognizer: 'Form Recognizer OCR',
  aiExtraction: 'AI extraction',
  'excel-parser': 'Excel parser',
  'csv-parser': 'CSV parser',
  'text-parser': 'Text parser',
  schemaDriven: 'Schema-driven',
};

/** Email when the state row carries a mail messageId; SFTP when the path says so. */
function arrivedBy(state) {
  if (state?.messageId) return 'Email';
  const path = `${state?.sourceBlobPath || ''} ${state?.archivedBlobPath || ''}`;
  if (/sftp/i.test(path)) return 'SFTP';
  return null;
}

/**
 * Bottom sheet for one issue on phones: the error up top, the facts that
 * decide what to do (received, arrived by, extraction), then the quick
 * actions as 48px rows. Every handler is DlqSection's own — this is only the
 * phone presentation of the detail pane. The long-form tools (AI replay,
 * reassign, timeline, engineering replays) stay in the full detail view,
 * one row away.
 */
export default function DlqIssueSheet({
  open,
  onOpenChange,
  row,
  detail,
  detailLoading,
  locationName,
  categoryLabel,
  statusLabel = {},
  actionNotes,
  onNotesChange,
  actionFeedback,
  actionInProgress,
  onAction,
  onViewImport,
  onEditConfig,
  onMoreDetails,
}) {
  const state = detail?.state || row;
  if (!state) return null;

  const stage = stageLabel(state.lastErrorStage);
  const category = state.lastErrorCategory ? categoryLabel(state.lastErrorCategory) : null;
  const message = (state.lastErrorMessage || '').trim();
  const extraction = state.ocrService ? (EXTRACTION_LABEL[state.ocrService] || state.ocrService) : null;
  const received = state.receivedAt ? `${clockTime(state.receivedAt)} · ${timeAgo(state.receivedAt)}` : null;
  const canViewImport = Boolean(detail?.state && (
    detail.state.importId || detail.state.archivedBlobPath
    || detail.state.imports_nightAuditPath || detail.state.sourceBlobPath
  ));
  const busy = actionInProgress || detailLoading || !detail;

  return (
    <ActionSheet
      open={open}
      onOpenChange={onOpenChange}
      title={issueFileName(state)}
      titleClassName="ep-mono"
      subtitle={[locationName, state.fileType, timeAgo(state.lastTransitionAt || state.receivedAt)].filter(Boolean).join(' · ')}
      meta={<RunStatePill status={state.status} label={statusLabel[state.status]} />}
    >
      {(stage || category || message) ? (
        <div className="ep-m-error" role="note">
          {(stage || category) ? (
            <span className="ep-m-error-stage">{[stage, category].filter(Boolean).join(' · ')}</span>
          ) : null}
          {message || 'No error message was recorded.'}
        </div>
      ) : null}

      <SheetKv
        rows={[
          { label: 'Received', value: received },
          { label: 'Arrived by', value: arrivedBy(state) },
          { label: 'Extraction', value: extraction },
          { label: 'Attempts', value: state.attempts != null ? String(state.attempts) : null },
        ]}
      />

      <textarea
        className="ep-m-note"
        value={actionNotes}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder="Note (required to resolve or escalate)"
        aria-label="Action note"
        rows={2}
      />

      {actionFeedback ? (
        <div className={`ep-m-feedback ep-tone-${actionFeedback.tone === 'error' ? 'error' : 'ok'}`} role="status">
          {actionFeedback.message}
        </div>
      ) : null}

      <SheetActions label="Issue actions">
        <SheetAction icon={PlayCircle} tone="primary" disabled={busy} onClick={() => onAction('replay')}>Replay</SheetAction>
        <SheetAction icon={RotateCcw} disabled={busy} onClick={() => onAction('retry_ocr')}>Retry OCR</SheetAction>
        <SheetAction icon={CheckCircle2} disabled={busy} onClick={() => onAction('mark_resolved')}>Mark resolved</SheetAction>
        <SheetAction icon={Flag} disabled={busy} onClick={() => onAction('escalate')}>Escalate</SheetAction>
        <SheetAction
          icon={Trash2}
          disabled={busy || !detail?.state?.messageId}
          hint={detail && !detail.state?.messageId ? 'No message id' : undefined}
          onClick={() => onAction('undo_dedupe')}
        >
          Undo dedupe
        </SheetAction>
      </SheetActions>

      <SheetActions label="More">
        {canViewImport ? (
          <SheetAction icon={Eye} onClick={onViewImport}>
            {detail.state.importId ? 'View import data' : 'View source file'}
          </SheetAction>
        ) : null}
        {detail ? (
          <SheetAction icon={Settings} onClick={onEditConfig}>Edit feed config</SheetAction>
        ) : null}
        <SheetAction icon={MoreHorizontal} onClick={onMoreDetails} hint="AI replay, reassign, timeline">
          Full details
        </SheetAction>
      </SheetActions>
    </ActionSheet>
  );
}
